import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const EmailConfirmation = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isTokenMode, setIsTokenMode] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('id');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      setIsTokenMode(true);
      confirmEmailWithToken(tokenFromUrl);
    }
  }, [location]);

  const confirmEmailWithToken = async (token) => {
    setIsVerifying(true);
    
    try {
      const response = await fetch('/api/v1/confirm_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFeedback({
          type: 'success',
          message: data.message || 'Email успешно подтвержден. Пожалуйста, установите постоянный пароль для своей учетной записи.'
        });
        
        // If we have the email, set it
        if (data.email) {
          setEmail(data.email);
        }
        
        // Instead of redirecting, show password setting form
        setIsConfirmed(true);
      } else {
        setFeedback({
          type: 'error',
          message: data.message || 'Не удалось подтвердить email'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Произошла ошибка при подтверждении email'
      });
      console.error('Error confirming email:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      setEmail(value);
      setError('');
    } else if (name === 'password') {
      setPassword(value);
      if (errors.password) {
        setErrors(prev => ({ ...prev, password: '' }));
      }
    } else if (name === 'passwordConfirm') {
      setPasswordConfirm(value);
      if (errors.passwordConfirm) {
        setErrors(prev => ({ ...prev, passwordConfirm: '' }));
      }
    }
    
    if (feedback.message) {
      setFeedback({ type: '', message: '' });
    }
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setError('Обязательное поле');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Пожалуйста, введите корректный email');
      return false;
    }
    
    return true;
  };
  
  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!password.trim()) {
      newErrors.password = 'Обязательное поле';
    } else if (password.length < 6) {
      newErrors.password = 'Пароль должен содержать не менее 6 символов';
    }
    
    if (!passwordConfirm.trim()) {
      newErrors.passwordConfirm = 'Обязательное поле';
    } else if (password !== passwordConfirm) {
      newErrors.passwordConfirm = 'Пароли не совпадают';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) {
      return;
    }
    
    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });
    
    try {
      const response = await fetch('/api/v1/reconfirm_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFeedback({
          type: 'success',
          message: data.message || 'Письмо с инструкциями по подтверждению отправлено на вашу электронную почту'
        });
        setEmail(''); // Clear the form
      } else {
        setFeedback({
          type: 'error',
          message: data.message || 'Не удалось отправить письмо подтверждения'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Произошла ошибка при запросе письма подтверждения'
      });
      console.error('Error requesting confirmation email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    setIsPasswordSubmitting(true);
    setFeedback({ type: '', message: '' });
    
    try {
      const response = await fetch('/api/v1/recover_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          recover_password: password,
          recover_password_repeat: passwordConfirm
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFeedback({
          type: 'success',
          message: data.message || 'Пароль успешно установлен'
        });
        
        // Clear password fields
        setPassword('');
        setPasswordConfirm('');
        
        // Wait a bit before redirecting to login
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setFeedback({
          type: 'error',
          message: data.message || 'Не удалось установить пароль'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Произошла ошибка при установке пароля'
      });
      console.error('Error setting password:', error);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex justify-center my-5 py-5">
          <div className="w-full md:w-2/3 lg:w-1/2 bg-gray-200 py-5 px-5 rounded-lg pb-10 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c0834d]"></div>
            <p className="ml-3 text-gray-700">Подтверждение email...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-center my-5 py-5">
        <div className="w-full md:w-2/3 lg:w-1/2 bg-gray-200 py-5 px-5 rounded-lg pb-10">
          {feedback.message && (
            <div className={`mb-4 p-3 rounded ${feedback.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {feedback.message}
            </div>
          )}
          
          {!isTokenMode && !isConfirmed && (
            <form onSubmit={handleSubmit} className="w-full">
              <div className="flex flex-col items-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Подтверждение email</h2>
                
                <div className="w-full px-0 lg:px-3 mb-6">
                  <p className="text-gray-700 mb-4">
                    Введите адрес электронной почты для запроса нового письма подтверждения.
                  </p>
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="email" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Эл. почта
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    placeholder="Ваш email"
                    className={`w-full p-2 border rounded my-3 ${error ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {error && (
                    <div className="text-red-500 text-sm mt-1">{error}</div>
                  )}
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 bg-[#c0834d] text-white rounded hover:bg-[#a87442] transition-colors ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Отправка...' : 'Отправить письмо подтверждения'}
                  </button>
                </div>
                
                <div className="w-full px-0 lg:px-3 text-center">
                  <Link to="/login" className="text-blue-600 hover:underline">
                    Вернуться на страницу входа
                  </Link>
                </div>
              </div>
            </form>
          )}
          
          {/* Password setting form after email confirmation */}
          {isConfirmed && (
            <form onSubmit={handlePasswordSubmit} className="w-full">
              <div className="flex flex-col items-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Установка пароля</h2>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="confirmed-email" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Эл. почта
                  </label>
                  <input
                    type="email"
                    id="confirmed-email"
                    name="confirmed-email"
                    value={email}
                    className="w-full p-2 border rounded my-3 bg-gray-100"
                    disabled
                  />
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="password" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Пароль
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={handleChange}
                    placeholder="Введите пароль"
                    className={`w-full p-2 border rounded my-3 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {errors.password && (
                    <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="passwordConfirm" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Подтверждение пароля
                  </label>
                  <input
                    type="password"
                    id="passwordConfirm"
                    name="passwordConfirm"
                    value={passwordConfirm}
                    onChange={handleChange}
                    placeholder="Повторите пароль"
                    className={`w-full p-2 border rounded my-3 ${errors.passwordConfirm ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {errors.passwordConfirm && (
                    <div className="text-red-500 text-sm mt-1">{errors.passwordConfirm}</div>
                  )}
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <button
                    type="submit"
                    disabled={isPasswordSubmitting}
                    className={`w-full py-2 px-4 bg-[#c0834d] text-white rounded hover:bg-[#a87442] transition-colors ${isPasswordSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isPasswordSubmitting ? 'Отправка...' : 'Установить пароль'}
                  </button>
                </div>
              </div>
            </form>
          )}
          
          {isTokenMode && !feedback.message && !isConfirmed && (
            <div className="w-full text-center py-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c0834d] mx-auto mb-4"></div>
              <p className="text-gray-700">Обработка запроса на подтверждение email...</p>
            </div>
          )}
          
          {isTokenMode && feedback.type === 'error' && (
            <div className="w-full text-center mt-4">
              <Link to="/confirm_email" className="text-blue-600 hover:underline">
                Запросить новое письмо подтверждения
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmation; 